import { zodResolver } from '@hookform/resolvers/zod'
import { useQuery } from '@tanstack/react-query'
import { LayoutGrid } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { applyServerErrors } from '@/components/forms/applyServerErrors'
import { FormField } from '@/components/forms/FormField'
import { LoadingState } from '@/components/feedback/States'
import { toast } from '@/components/feedback/toast-store'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { shiftsQuery, staffOptionsQuery, useSaveSection, type SectionInput } from '../api'
import { sectionLabel } from '../sectionLabel'
import type { Section } from '../types'
import { FormDialog } from './FormDialog'

const optionalId = z.string().transform((v) => (v === '' ? null : Number(v)))

/**
 * An unnamed section means the whole class is taught as one group ("Terminale OSE"); the API only
 * accepts it as the class's only section.
 */
function sectionSchema(canBeUnnamed: boolean) {
  const name = z.string().trim().max(20, 'Use at most 20 characters.')
  return z.object({
    name: canBeUnnamed ? name : name.min(1, 'Enter the section name.'),
    capacity: z
      .string()
      .trim()
      .refine(
        (v) => v === '' || (/^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 1000),
        'Enter a capacity between 1 and 1000, or leave blank.',
      )
      .transform((v) => (v === '' ? null : Number(v))),
    classTeacherId: optionalId,
    shiftId: optionalId,
    room: z
      .string()
      .trim()
      .max(30, 'Use at most 30 characters.')
      .transform((v) => (v === '' ? null : v)),
  })
}

type SectionFormInput = z.input<ReturnType<typeof sectionSchema>>
const FIELDS = ['name', 'capacity', 'classTeacherId', 'shiftId', 'room'] as const
const FORM_ID = 'section-form'

interface SectionDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  classId: number
  className: string
  section?: Section | null
  /** Suggested name for a new section (next free letter). */
  suggestedName?: string
  /** True when the class has no other section, so this one may be left unnamed (whole class). */
  canBeUnnamed?: boolean
}

export function SectionDialog({
  open,
  onOpenChange,
  classId,
  className,
  section,
  suggestedName = '',
  canBeUnnamed = false,
}: SectionDialogProps) {
  const { t } = useTranslation('academic')
  const save = useSaveSection()
  return (
    <FormDialog
      open={open}
      onOpenChange={onOpenChange}
      icon={<LayoutGrid />}
      title={
        section
          ? section.name
            ? t('Edit section {{name}}', { name: section.name })
            : t('Edit {{name}}', { name: className })
          : t('New section')
      }
      description={t('{{className}} · students are enrolled into a section.', { className })}
      formId={FORM_ID}
      submitLabel={section ? t('Save changes') : t('Add section')}
      pending={save.isPending}
    >
      <SectionForm
        canBeUnnamed={canBeUnnamed}
        defaults={{
          name: section?.name ?? suggestedName,
          capacity: section ? (section.capacity?.toString() ?? '') : '30',
          classTeacherId: section?.classTeacher ? String(section.classTeacher.id) : '',
          shiftId: section?.shift ? String(section.shift.id) : '',
          room: section?.room ?? '',
        }}
        onSubmit={async (values) => {
          const saved = await save.mutateAsync({ ...values, classId, id: section?.id })
          toast.success(
            section ? t('Section updated') : t('Section added'),
            sectionLabel(className, saved.name, ' · '),
          )
          onOpenChange(false)
        }}
      />
    </FormDialog>
  )
}

function SectionForm({
  canBeUnnamed,
  defaults,
  onSubmit,
}: {
  canBeUnnamed: boolean
  defaults: SectionFormInput
  onSubmit: (values: SectionInput) => Promise<void>
}) {
  const { t } = useTranslation('academic')
  const staff = useQuery(staffOptionsQuery())
  const shifts = useQuery(shiftsQuery())
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<SectionFormInput, unknown, SectionInput>({
    resolver: zodResolver(sectionSchema(canBeUnnamed)),
    defaultValues: defaults,
  })

  const submit = handleSubmit(async (values) => {
    try {
      await onSubmit(values)
    } catch (e) {
      applyServerErrors(e, setError, FIELDS)
    }
  })

  // Native selects only apply the default once their options exist, so wait for the lookups.
  if (staff.isPending || shifts.isPending)
    return <LoadingState label={t('Loading staff and shifts…')} className="min-h-40" />

  return (
    <form id={FORM_ID} onSubmit={(e) => void submit(e)} noValidate className="grid gap-4 sm:grid-cols-2">
      <FormField
        label={t('Section name')}
        error={errors.name?.message}
        required={!canBeUnnamed}
        hint={
          canBeUnnamed
            ? t('Leave empty if the whole class is taught as one group, e.g. Terminale OSE.')
            : undefined
        }
      >
        <Input placeholder="A" autoComplete="off" {...register('name')} />
      </FormField>
      <FormField label={t('Capacity')} error={errors.capacity?.message} hint={t('Leave blank for no limit.')}>
        <Input type="number" inputMode="numeric" min={1} max={1000} {...register('capacity')} />
      </FormField>
      <FormField label={t('Class teacher')} error={errors.classTeacherId?.message} className="sm:col-span-2">
        <Select
          placeholder={t('Not assigned')}
          options={(staff.data ?? []).map((s) => ({ value: s.id, label: `${s.name} · ${s.employeeCode}` }))}
          {...register('classTeacherId')}
        />
      </FormField>
      <FormField label={t('Shift')} error={errors.shiftId?.message}>
        <Select
          placeholder={t('No shift')}
          options={(shifts.data ?? []).map((s) => ({
            value: s.id,
            label: s.startTime && s.endTime ? `${s.name} (${s.startTime}–${s.endTime})` : s.name,
          }))}
          {...register('shiftId')}
        />
      </FormField>
      <FormField label={t('Room')} error={errors.room?.message}>
        <Input placeholder="R-101" autoComplete="off" {...register('room')} />
      </FormField>
    </form>
  )
}
